import React from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthProvider';
import Loading from '../../components/Loading';
import { useGetDocuSignEnvelopeSignedStatusByInvoiceOrGroupIdQuery } from '../../generated/graphql';
import CustomerAgreement from './CustomerAgreement';
import TnCSignedFlow from './TnCSignedFlow';

interface CompleteParams {
  invoiceId: string;
}

const Complete: React.FC = (props) => {
  const { isAuthenticated: isBrokerFlow } = useAuth();
  const propsObj = JSON.parse(JSON.stringify(props));
  const state = propsObj['history']?.['location']?.['state'];

  // [TODO][Phase-2][Multi-prod] Rename `invoiceId` to `invoiceOrGroupId` (and
  // update CompleteParams / route definition) once stripe success url return is updated.
  const { invoiceId } = useParams<CompleteParams>();

  const {
    data: docusignData,
    error: getDocusignDataError,
    loading: getDocusignDataLoading
  } = useGetDocuSignEnvelopeSignedStatusByInvoiceOrGroupIdQuery({
    variables: {
      invoiceOrGroupId: invoiceId
    }
  });

  if (getDocusignDataError) {
    return (
      <div data-testid="error-msg" className="m-auto text-lg text-center">
        {"Sorry, there's an error while getting the Terms & Conditions status."}
      </div>
    );
  }

  if (getDocusignDataLoading || !docusignData) {
    return <Loading />;
  }

  // Broker bind: TnC not signed

  // Per-LOB map keyed by lobType, e.g.
  // { GL: { isDocuSignEnvelopeSigned: true }, EO: { isDocuSignEnvelopeSigned: false } }.
  // All LOBs must agree; mixed signed/unsigned is invalid.
  // Backend forwards owner.data.isDocuSignEnvelopeSigned as-is: true = signed,
  // false = unsigned, null/undefined = never initiated (treated as not-unsigned).
  const docusignStatusByLob: Record<
    string,
    { isDocuSignEnvelopeSigned?: boolean | null } | null | undefined
  > = docusignData.getDocuSignEnvelopeSignedStatusByInvoiceOrGroupId ?? {};
  const docusignStatuses = Object.values(docusignStatusByLob);

  if (docusignStatuses.length === 0) {
    return (
      <div data-testid="error-msg" className="m-auto text-lg text-center">
        {"Sorry, we couldn't determine the Terms & Conditions signing status."}
      </div>
    );
  }

  const allUnsigned = docusignStatuses.every(
    (status) => status?.isDocuSignEnvelopeSigned === false
  );
  const hasUnsignedLob = docusignStatuses.some(
    (status) => status?.isDocuSignEnvelopeSigned === false
  );

  if (hasUnsignedLob && !allUnsigned) {
    return (
      <div data-testid="error-msg" className="m-auto text-lg text-center">
        {
          'Sorry, the Terms & Conditions signing status is inconsistent across products.'
        }
      </div>
    );
  }

  if (allUnsigned) {
    if (isBrokerFlow) {
      return <CustomerAgreement invoiceId={invoiceId} />;
    } else {
      return (
        <div className="m-auto text-lg text-center" data-testid="TnC-message">
          <p>
            <b className="text-xl">
              Thank you for submitting your payment! Please sign the Terms &
              Conditions to proceed further.
            </b>
          </p>
          <p className="mt-4">
            If you have any questions, please contact your broker/agent.
          </p>
        </div>
      );
    }
  }

  return <TnCSignedFlow flowKind={state?.flowKind} />;
};

export const QuickComplete: React.FC = () => (
  <div className="m-auto text-lg text-center">
    <div>
      <p>
        <b data-testid="thank-you-text" className="text-xl">
          Thank you, your Policy details with Foxquilt have been updated.
        </b>
      </p>
      <p>
        If you have any questions, contact us at{' '}
        <a
          href="tel:+18888784588"
          data-testid="phone-number"
          className="underline"
        >
          1-888-878-4588
        </a>{' '}
        or{' '}
        <a
          href="mailto:support@foxquilt.com"
          data-testid="email-address"
          className="underline"
        >
          support@foxquilt.com
        </a>
        .
      </p>
    </div>
  </div>
);

export default Complete;
